import { isBlank, isNone, isPresent } from '@ember/utils';
import { Country } from '@square/dashboard-data/models/squareup/common/countries';
import camelcaseKeys from 'camelcase-keys';
function getCountryCode(globalAddress, currentUserCountryCode) {
  return (globalAddress.countryCode || currentUserCountryCode)?.toUpperCase();
}
export function createNewAddressWithPrefectureAssigned(sourceAddress, countryCode, prefecture) {
  if (!sourceAddress || !countryCode || !prefecture) {
    return sourceAddress;
  }
  const newAddress = {};
  for (const key in sourceAddress) {
    const shouldAssignPrefecture = key === 'administrativeDistrictLevel1' && countryCode === Country.JP;
    newAddress[key] = shouldAssignPrefecture ? prefecture : sourceAddress[key];
  }
  return newAddress;
}

// TODO(RETAIL-9939): Investigate using `adresss.hasOwnProperty` to check if an address is a GlobalAddress
export function isGlobalAddress(address) {
  if (isNone(address)) {
    return false;
  }

  // NB(sluong): We need to check if `this.address` is a GlobalAddress so that we can properly get and set the relevant
  // properties. The ideal solution is to use `hasOwnProperty`, but that doesn't work for currently unknown reasons, and
  // will be investigated in RETAIL-9939. In the meantime, the code below attempts to accomplish the same thing. This is
  // just a best-effort solution that is the closest we can get without having `hasOwnProperty` available.
  if (address.administrativeDistrictLevel1 === undefined) {
    if (typeof address.getAllProperties === 'function') {
      return Object.keys(address.getAllProperties()).includes('administrativeDistrictLevel1');
    }
    return address.constructor?.attributes?.has('administrativeDistrictLevel1');
  }
  return true;
}
export function formatMultilineAddress(globalAddress, currentUserCountryCode) {
  if (isNone(globalAddress)) {
    return null;
  }
  const countryCode = getCountryCode(globalAddress, currentUserCountryCode);
  return getAddressParts(globalAddress, countryCode).join('\n');
}
export function formatSingleLineAddress(globalAddress, currentUserCountryCode) {
  if (!globalAddress) {
    return null;
  }
  const countryCode = getCountryCode(globalAddress, currentUserCountryCode);
  const addressParts = getAddressParts(globalAddress, countryCode);
  return countryCode === Country.JP ? addressParts.join(' ') : addressParts.join(', ');
}
export function getLocationAddressFieldJP(merchantProfile, field) {
  // Map the fields as currently used by Web's addresses table
  switch (field) {
    case 'addressLine1':
      return merchantProfile?.addressLine3;
    case 'sublocality':
      return merchantProfile?.street1;
    default:
      return merchantProfile?.[field];
  }
}
export function getJpAddressFromScriptAddress(scriptAddress) {
  const camelCasedScriptAddress = camelcaseKeys(scriptAddress);
  const address = {
    addressLine1: camelCasedScriptAddress?.addressLine1,
    addressLine2: camelCasedScriptAddress?.addressLine2,
    locality: camelCasedScriptAddress?.locality,
    adminLevel1: camelCasedScriptAddress?.administrativeDistrictLevel1,
    postalCode: camelCasedScriptAddress?.postalCode,
    sublocality: camelCasedScriptAddress?.sublocality,
    countryCode: camelCasedScriptAddress?.countryCode
  };
  return address;
}
export function getAddressFromMerchantProfile(merchantProfile) {
  const address = {
    addressLine1: merchantProfile?.street1,
    addressLine2: merchantProfile?.street2,
    addressLine3: merchantProfile?.addressLine3,
    locality: merchantProfile?.city,
    adminLevel1: merchantProfile?.state,
    postalCode: merchantProfile?.postalCode,
    agrexCode: merchantProfile?.addressCode,
    countryCode: merchantProfile?.countryCode
  };

  // context : https://docs.google.com/document/d/1I7x-vnqeZ6zNHw3TfX_RHgM3sSV0DoXVC95sg0HeM1c/edit#
  if (merchantProfile?.countryCode === 'JP') {
    address.addressLine1 = merchantProfile?.addressLine3;
    address.sublocality = merchantProfile?.street1;
    delete address.addressLine3;
    delete address.addressLine2;
  }
  return address;
}
export function getMerchantProfileFromAddress(address) {
  const merchantProfile = {
    street1: address?.addressLine1,
    street2: address?.addressLine2,
    addressLine3: address?.addressLine3,
    city: address?.locality,
    state: address?.adminLevel1,
    postalCode: address?.postalCode,
    addressCode: address?.agrexCode,
    countryCode: address?.countryCode
  };

  // context : https://docs.google.com/document/d/1I7x-vnqeZ6zNHw3TfX_RHgM3sSV0DoXVC95sg0HeM1c/edit#
  if (address?.countryCode === 'JP') {
    merchantProfile.street1 = address?.sublocality;
    merchantProfile.addressLine3 = address?.addressLine1;
    delete merchantProfile.street2;
  }
  return merchantProfile;
}
export function getAddressParts(globalAddress, countryCode) {
  if (isNone(globalAddress)) {
    return [];
  }
  let parts;
  switch (countryCode) {
    case Country.JP:
      parts = getAddressPartsJP(globalAddress);
      break;
    case Country.IE:
      parts = getAddressPartsIE(globalAddress);
      break;
    case Country.ES:
      parts = getAddressPartsES(globalAddress);
      break;
    case Country.FR:
      parts = getAddressPartsFR(globalAddress);
      break;
    case Country.PT:
      parts = getAddressPartsPT(globalAddress);
      break;
    default:
      parts = getAddressPartsDefault(globalAddress);
  }

  // Using negative of isBlank since original implementation was
  // parts.reject(isBlank), and not sure who depends on that specific
  // behavior which is different from isPresent
  return parts.filter(value => !isBlank(value));
}
export function getAddressLastLine(globalAddress, countryCode) {
  const administrativeDistrictLevel1 = globalAddress.administrativeDistrictLevel1 || '';
  const locality = globalAddress.locality || '';
  const postalCode = globalAddress.postalCode || '';
  switch (countryCode) {
    case Country.PT:
      {
        const postalAndLocality = [postalCode, locality].filter(isPresent).join(' ');
        return postalAndLocality.trim();
      }
    case Country.ES:
      {
        const postalAndLocality = [postalCode, locality].filter(isPresent).join(' ');
        const separator = postalAndLocality.length > 0 && administrativeDistrictLevel1 ? ', ' : '';
        return `${postalAndLocality}${separator}${administrativeDistrictLevel1}`.trim();
      }
    default:
      {
        const lastLineWithLocality = `${locality}, ${administrativeDistrictLevel1} ${postalCode}`;
        const lastLineWithoutLocality = `${administrativeDistrictLevel1} ${postalCode}`;
        return (globalAddress.locality ? lastLineWithLocality : lastLineWithoutLocality).trim();
      }
  }
}
function getAddressPartsJP(globalAddress) {
  const postalCode = globalAddress.postalCode;
  let postalCodeWithPostalPrefix = postalCode;
  // do not prefix undefined, null, or empty string
  if (postalCode) {
    postalCodeWithPostalPrefix = postalCode?.charAt(0) !== '〒' ? `〒${postalCode}` : postalCode;
  }
  return [postalCodeWithPostalPrefix, globalAddress.administrativeDistrictLevel1, globalAddress.administrativeDistrictLevel2, globalAddress.administrativeDistrictLevel3, globalAddress.locality, globalAddress.sublocality, globalAddress.sublocality1, globalAddress.sublocality2, globalAddress.sublocality3, globalAddress.sublocality4, globalAddress.sublocality5, globalAddress.addressLine1, globalAddress.addressLine2, globalAddress.addressLine3, globalAddress.addressLine4, globalAddress.addressLine5];
}
function getAddressPartsIE(globalAddress) {
  return [globalAddress.addressLine1, globalAddress.addressLine2, globalAddress.locality, globalAddress.administrativeDistrictLevel1 ? `Co. ${globalAddress.administrativeDistrictLevel1}` : null, globalAddress.postalCode];
}
function getAddressPartsES(globalAddress) {
  return [globalAddress.addressLine1, globalAddress.addressLine2, getAddressLastLine(globalAddress, Country.ES)];
}
function getAddressPartsFR(globalAddress) {
  const postalAndLocality = [globalAddress.postalCode, globalAddress.locality].filter(isPresent).join(' ');
  return [globalAddress.addressLine1, globalAddress.addressLine2, postalAndLocality];
}
function getAddressPartsPT(globalAddress) {
  return [globalAddress.addressLine1, globalAddress.addressLine2, getAddressLastLine(globalAddress, Country.PT)];
}
function getAddressPartsDefault(globalAddress) {
  return [globalAddress.addressLine1, globalAddress.addressLine2, globalAddress.addressLine3, globalAddress.addressLine4, globalAddress.addressLine5, getAddressLastLine(globalAddress)];
}